import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type { GenericArray } from '@Types';
// NOTE: this needs to be directed imported
import useADialog from '@/components/atoms/ADialog/useADialog';
import { useSessionStore } from '@Stores';
import { getApplicationStatusChangeModalProps } from '@PeopleModule';

export default function () {
  const { shelterProfile } = storeToRefs(useSessionStore());

  const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
  const { t } = useI18n();

  function onOpenAdoptApplicationStatusModal(application: any, adopterStatusModel?: any, isMultiple?: boolean) {
    if (!adopterStatusModel) return;

    const contentTarget: GenericArray = {
      canceled: t('adoption.modal.application.canceled'),
      declined: t('adoption.modal.application.declined'),
      expired: t('adoption.modal.application.expired'),
      submitted: t('adoption.modal.application.submitted'),
      approved: t('adoption.modal.application.approved'),
      pending: t('adoption.modal.application.pending'),
      'do not adopt': t('adoption.modal.application.doNotAdopt'),
    };

    const { adopt_user_id, application_id, _id, people_id, full_name, status, adopter_status, people } = application;
    const currentStatus = status ?? adopter_status;

    let content: string;
    let secondContent = '';
    let confirmWordKey: string | undefined;
    let isDeclinedApplication = false;

    if (!isMultiple) {
      const modalProps = getApplicationStatusChangeModalProps({
        oldStatus: currentStatus ?? '',
        newStatus: adopterStatusModel,
        formType: 'adoption',
        adopterStatus: adopter_status ?? application?.adopter_status,
        doNotAdopt: people?.do_not_adopt ?? application?.do_not_adopt,
        contentTarget,
        t,
        shelterProfile: shelterProfile?.value,
      });
      content = modalProps.content;
      secondContent = modalProps.secondContent;
      confirmWordKey = modalProps.confirmWordKey;
      isDeclinedApplication = modalProps.isDeclinedApplication;
    } else {
      secondContent =
        (adopterStatusModel === 'approved' && shelterProfile?.value?.application_approved_email_status !== false) ||
        (adopterStatusModel === 'canceled' && shelterProfile?.value?.application_canceled_email_status !== false) ||
        (adopterStatusModel === 'declined' && shelterProfile?.value?.application_declined_email_status !== false)
          ? t('adoption.modal.application.emailSent', { status: adopterStatusModel })
          : '';
      content = t('adoption.modal.areYouSure', { action: contentTarget[adopterStatusModel] });
      isDeclinedApplication =
        adopterStatusModel === 'declined' && shelterProfile?.value?.application_declined_email_status !== false;
    }

    let payload: any = {
      adopt_user_id: adopt_user_id?.$oid ?? adopt_user_id,
      application_id: application_id?.$oid ?? application_id,
      form_type: 'adoption',
      old_status: currentStatus,
      people_id: people_id ?? _id?.$oid ?? _id,
      adopter_name: full_name,
      status: adopterStatusModel,
      shelter_id: shelterProfile.value?._id?.$oid,
    };

    if (isMultiple) {
      const selectedItems = application.map((item: any) => ({
        adopt_user_id: item?.adopt_user_id?.$oid,
        application_id: item?.application_id?.$oid,
        form_type: item.form_type,
        people_id: item.people_id?.$oid || item.people_id,
        shelter_id: item.shelter_id?.$oid,
      }));

      payload = {
        applications: selectedItems,
        old_status: '',
        status: adopterStatusModel,
      };
    }

    const personData =
      !isMultiple && adopterStatusModel === 'do not adopt' ? { groups: people?.groups ?? [] } : undefined;

    activeModalProps.value = {
      clickEvent: payload,
      isMultiple,
      secondContent,
      content,
      personData,
      ...(confirmWordKey && { confirmWordKey }),
    };

    activeModal.value = isDeclinedApplication ? SYSTEM_MODALS.APPLICATION_DECLINED : SYSTEM_MODALS.BASIC_MODAL;
    dialogVisibility.value = true;
  }

  function onOpenOtherApplicationStatusModal(application?: any, statusModel?: any, isMultiple?: boolean) {
    if (!statusModel) return;

    const contentTarget: GenericArray = {
      canceled: t('adoption.modal.application.canceled'),
      declined: t('adoption.modal.application.declined'),
      expired: t('adoption.modal.application.expired'),
      submitted: t('adoption.modal.application.submitted'),
      approved: t('adoption.modal.application.approved'),
      pending: t('adoption.modal.application.pending'),
      deleted: t('filters.options.deleted'),
    };

    const { adopt_user_id, application_id, _id, status, form_type } = application;

    let payload: any = {
      adopt_user_id: adopt_user_id?.$oid,
      application_id: application_id?.$oid,
      form_type,
      old_status: status,
      people_id: _id?.$oid || _id,
      status: statusModel,
      shelter_id: shelterProfile.value?._id?.$oid,
    };

    if (isMultiple) {
      payload = {
        applications: [application],
        old_status: '',
        status: statusModel,
      };
    }

    activeModalProps.value = {
      clickEvent: payload,
      isMultiple,
      content: t('adoption.modal.areYouSure', {
        action: contentTarget[statusModel],
      }),
    };

    activeModal.value = SYSTEM_MODALS.BASIC_MODAL;
    dialogVisibility.value = true;
  }

  function useOptions(isFormsAndApplications: boolean | 'profile' = false) {
    if (isFormsAndApplications === 'profile') {
      return [
        { label: 'filters.options.approved', value: 'approved' },
        { label: 'filters.options.submitted', value: 'submitted' },
        { label: 'filters.options.pending', value: 'pending' },
        { label: 'filters.options.canceled', value: 'canceled' },
        { label: 'filters.options.declined', value: 'declined' },
        { label: 'filters.options.expired', value: 'expired' },
        { label: 'filters.options.doNotAdopt', value: 'do not adopt' },
      ];
    }
    return [
      { label: 'filters.options.submitted', value: 'submitted' },
      { label: 'filters.options.pending', value: 'pending' },
      { label: 'filters.options.approved', value: 'approved' },
      { label: 'filters.options.canceled', value: 'canceled' },
      { label: 'filters.options.declined', value: 'declined' },
      { label: 'filters.options.expired', value: 'expired' },
      {
        label: isFormsAndApplications ? 'filters.options.deleted' : 'filters.options.doNotAdopt',
        value: isFormsAndApplications ? 'deleted' : 'do not adopt',
      },
    ];
  }

  return { onOpenAdoptApplicationStatusModal, onOpenOtherApplicationStatusModal, useOptions };
}
